import type { EexMatrixDeliveryPeriod } from "./eex-live-market";

export type EexForwardCurvePriceBasis = "midpoint" | "bid" | "ask" | "last";
export type EexForwardCurveHorizon = "front" | "12m" | "24m" | "all" | "custom";

export type EexForwardCurvePriceRow = {
  bidPrice: string | null;
  askPrice: string | null;
  midpoint: string | null;
  lastTradePrice: string | null;
};

export type EexForwardCurveScopedSubject = {
  scopeId: string;
  subjectId: string;
  apiFamily: string;
};

export type EexForwardCurveScopedQuote = EexForwardCurveScopedSubject & {
  coverageState: "unknown" | "quoted" | "unquoted" | "omitted";
  marketTimestamp: string | null;
  receivedAt: string;
  storageMode?: "live_only" | "snapshots" | "publication_events" | null;
};

export type EexForwardCurveDeliveryWindow = {
  startMs: number;
  endExclusiveMs: number;
  midpointMs: number;
};

export type EexForwardCurveTimeDomain = {
  startMs: number;
  endExclusiveMs: number;
};

export type EexForwardCurveMonthRange = {
  fromMonth: string;
  throughMonth: string;
};

/**
 * Historical reads are exact-scope and fail closed. Prefer the best quote from
 * a scope allowed to serve retained Curve-comparison rows instead of blindly
 * reusing a fresher scope that has no accessible stored history.
 */
export function eexForwardCurveHistorySubject(
  instrument: EexForwardCurveScopedSubject,
  quotes: readonly EexForwardCurveScopedQuote[],
  historyScopeIds: ReadonlySet<string>
): Pick<EexForwardCurveScopedSubject, "scopeId" | "subjectId"> | null {
  let selected: EexForwardCurveScopedQuote | null = null;
  for (const quote of quotes) {
    if (
      quote.apiFamily !== instrument.apiFamily
      || quote.subjectId !== instrument.subjectId
      || (!historyScopeIds.has(quote.scopeId) && quote.storageMode !== "snapshots")
      || quote.storageMode === "live_only"
    ) continue;
    if (!selected || compareHistoryQuotes(quote, selected) > 0) selected = quote;
  }
  if (selected) return { scopeId: selected.scopeId, subjectId: selected.subjectId };
  const explicitlyLiveOnly = quotes.some((quote) =>
    quote.apiFamily === instrument.apiFamily
      && quote.scopeId === instrument.scopeId
      && quote.subjectId === instrument.subjectId
      && quote.storageMode === "live_only"
  );
  return historyScopeIds.has(instrument.scopeId) && !explicitlyLiveOnly
    ? { scopeId: instrument.scopeId, subjectId: instrument.subjectId }
    : null;
}

export function eexForwardCurvePrice(
  row: EexForwardCurvePriceRow | null | undefined,
  basis: EexForwardCurvePriceBasis
): string | null {
  if (!row) return null;
  if (basis === "bid") return row.bidPrice;
  if (basis === "ask") return row.askPrice;
  if (basis === "last") return row.lastTradePrice;
  return row.midpoint;
}

export function eexForwardCurveNumericPrice(value: string | null): number | null {
  if (value == null || !/^[+-]?(?:\d+(?:\.\d*)?|\.\d+)$/.test(value.trim())) return null;
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : null;
}

/** Select the requested tenor horizon, then order every contract on one delivery timeline. */
export function eexForwardCurvePeriods(
  periods: readonly EexMatrixDeliveryPeriod[],
  availablePeriodKeys: ReadonlySet<string>,
  horizon: EexForwardCurveHorizon,
  displayDomain: EexForwardCurveTimeDomain | null = null
): EexMatrixDeliveryPeriod[] {
  const chronological = availableForwardPeriods(periods, availablePeriodKeys);
  if (horizon === "all") return chronological.slice(0, 64);
  if (horizon !== "front") {
    if (!displayDomain) return [];
    const matching = chronological.filter((period) =>
      eexForwardCurvePeriodOverlapsDomain(period, displayDomain)
    );
    return horizon === "custom" && matching.length > 64 ? [] : matching.slice(0, 64);
  }

  const limits: Partial<Record<EexMatrixDeliveryPeriod["kind"], number>> = {
    month: 7,
    quarter: 6,
    season: 4,
    year: 4,
  };
  const used = new Map<EexMatrixDeliveryPeriod["kind"], number>();
  return chronological.filter((period) => {
    const limit = limits[period.kind] ?? 0;
    const count = used.get(period.kind) ?? 0;
    used.set(period.kind, count + 1);
    return count < limit;
  });
}

/** Return all available contracts intersecting an exact display domain. */
export function eexForwardCurvePeriodsInDomain(
  periods: readonly EexMatrixDeliveryPeriod[],
  availablePeriodKeys: ReadonlySet<string>,
  displayDomain: EexForwardCurveTimeDomain
): EexMatrixDeliveryPeriod[] {
  return availableForwardPeriods(periods, availablePeriodKeys)
    .filter((period) => eexForwardCurvePeriodOverlapsDomain(period, displayDomain));
}

/** Parse inclusive YYYY-MM inputs into an exact UTC delivery-time viewport. */
export function eexForwardCurveMonthDomain(
  fromMonth: string,
  throughMonth: string
): EexForwardCurveTimeDomain | null {
  const startMs = parseUtcMonth(fromMonth);
  const throughStartMs = parseUtcMonth(throughMonth);
  if (startMs == null || throughStartMs == null || throughStartMs < startMs) return null;
  const throughDate = new Date(throughStartMs);
  const endExclusiveMs = Date.UTC(
    throughDate.getUTCFullYear(),
    throughDate.getUTCMonth() + 1,
    1
  );
  return Number.isFinite(endExclusiveMs) && endExclusiveMs > startMs
    ? { startMs, endExclusiveMs }
    : null;
}

/** Format a valid display domain back into inclusive month-input values. */
export function eexForwardCurveDomainMonths(
  domain: EexForwardCurveTimeDomain | null
): EexForwardCurveMonthRange | null {
  if (!domain || domain.endExclusiveMs <= domain.startMs) return null;
  return {
    fromMonth: formatUtcMonth(domain.startMs),
    throughMonth: formatUtcMonth(domain.endExclusiveMs - 1),
  };
}

/** Pick the first candidate range that can be rendered exactly within a subject limit. */
export function eexForwardCurveFirstExactMonthRange(
  periods: readonly EexMatrixDeliveryPeriod[],
  availablePeriodKeys: ReadonlySet<string>,
  candidates: readonly (EexForwardCurveMonthRange | null)[],
  subjectLimit: number
): EexForwardCurveMonthRange | null {
  if (!Number.isInteger(subjectLimit) || subjectLimit < 0) return null;
  for (const candidate of candidates) {
    if (!candidate) continue;
    const domain = eexForwardCurveMonthDomain(candidate.fromMonth, candidate.throughMonth);
    if (!domain) continue;
    if (eexForwardCurvePeriodsInDomain(periods, availablePeriodKeys, domain).length <= subjectLimit) {
      return { ...candidate };
    }
  }
  return null;
}

/**
 * Build a rolling month viewport. It starts in the current delivery month when
 * a still-listed contract overlaps it, otherwise at the first future listed
 * delivery month. The fallback keeps old-but-still-returned catalogues usable.
 */
export function eexForwardCurveRollingDomain(
  periods: readonly EexMatrixDeliveryPeriod[],
  availablePeriodKeys: ReadonlySet<string>,
  monthCount: number,
  nowMs = Date.now()
): EexForwardCurveTimeDomain | null {
  if (!Number.isInteger(monthCount) || monthCount < 1 || !Number.isFinite(nowMs)) return null;
  const windows = availableForwardPeriods(periods, availablePeriodKeys)
    .flatMap((period) => {
      const window = eexForwardCurveDeliveryWindow(period);
      return window ? [window] : [];
    });
  if (windows.length === 0) return null;

  const now = new Date(nowMs);
  const currentMonthStartMs = Date.UTC(now.getUTCFullYear(), now.getUTCMonth(), 1);
  const intersectsCurrentMonth = windows.some((window) =>
    window.startMs <= currentMonthStartMs && window.endExclusiveMs > currentMonthStartMs
  );
  const firstFutureStartMs = windows
    .filter((window) => window.endExclusiveMs > currentMonthStartMs)
    .reduce((earliest, window) => Math.min(earliest, Math.max(window.startMs, currentMonthStartMs)), Number.POSITIVE_INFINITY);
  const earliestStartMs = windows.reduce(
    (earliest, window) => Math.min(earliest, window.startMs),
    Number.POSITIVE_INFINITY
  );
  const anchorMs = intersectsCurrentMonth
    ? currentMonthStartMs
    : Number.isFinite(firstFutureStartMs)
      ? firstFutureStartMs
      : earliestStartMs;
  const anchor = new Date(anchorMs);
  const startMs = Date.UTC(anchor.getUTCFullYear(), anchor.getUTCMonth(), 1);
  const endExclusiveMs = Date.UTC(
    anchor.getUTCFullYear(),
    anchor.getUTCMonth() + monthCount,
    1
  );
  return { startMs, endExclusiveMs };
}

export function eexForwardCurvePeriodOverlapsDomain(
  period: Pick<EexMatrixDeliveryPeriod, "deliveryStart" | "deliveryEnd">,
  domain: EexForwardCurveTimeDomain
): boolean {
  const window = eexForwardCurveDeliveryWindow(period);
  return window != null
    && window.startMs < domain.endExclusiveMs
    && window.endExclusiveMs > domain.startMs;
}

export function eexForwardCurveClipDeliveryWindow(
  window: EexForwardCurveDeliveryWindow,
  domain: EexForwardCurveTimeDomain
): EexForwardCurveDeliveryWindow | null {
  const startMs = Math.max(window.startMs, domain.startMs);
  const endExclusiveMs = Math.min(window.endExclusiveMs, domain.endExclusiveMs);
  return endExclusiveMs > startMs
    ? {
      startMs,
      endExclusiveMs,
      midpointMs: startMs + (endExclusiveMs - startMs) / 2,
    }
    : null;
}

/**
 * EEX delivery end dates are inclusive. Geometry uses the following UTC
 * midnight as the exclusive boundary so adjacent months meet exactly and a
 * quarter/calendar spans the exact union of its component delivery periods.
 */
export function eexForwardCurveDeliveryWindow(
  period: Pick<EexMatrixDeliveryPeriod, "deliveryStart" | "deliveryEnd">
): EexForwardCurveDeliveryWindow | null {
  const startMs = parseUtcDate(period.deliveryStart);
  const inclusiveEndMs = parseUtcDate(period.deliveryEnd);
  if (startMs == null || inclusiveEndMs == null || inclusiveEndMs < startMs) return null;
  const endExclusiveMs = inclusiveEndMs + UTC_DAY_MS;
  if (!Number.isFinite(endExclusiveMs)) return null;
  return {
    startMs,
    endExclusiveMs,
    midpointMs: startMs + (endExclusiveMs - startMs) / 2,
  };
}

export function eexForwardCurveTimeDomain(
  periods: readonly EexMatrixDeliveryPeriod[]
): EexForwardCurveTimeDomain | null {
  let startMs = Number.POSITIVE_INFINITY;
  let endExclusiveMs = Number.NEGATIVE_INFINITY;
  for (const period of periods) {
    const window = eexForwardCurveDeliveryWindow(period);
    if (!window) continue;
    startMs = Math.min(startMs, window.startMs);
    endExclusiveMs = Math.max(endExclusiveMs, window.endExclusiveMs);
  }
  return Number.isFinite(startMs) && Number.isFinite(endExclusiveMs) && endExclusiveMs > startMs
    ? { startMs, endExclusiveMs }
    : null;
}

export function eexForwardCurveRange(
  values: ReadonlyArray<number | null>
): { min: number; max: number } | null {
  const finite = values.filter((value): value is number => value != null && Number.isFinite(value));
  if (finite.length === 0) return null;
  const rawMin = Math.min(...finite);
  const rawMax = Math.max(...finite);
  const span = rawMax - rawMin;
  const padding = span === 0
    ? Math.max(Math.abs(rawMax) * 0.03, 1)
    : Math.max(span * 0.12, 0.05);
  return { min: rawMin - padding, max: rawMax + padding };
}

function compareHistoryQuotes(
  a: EexForwardCurveScopedQuote,
  b: EexForwardCurveScopedQuote
): number {
  const coverageRank = (state: EexForwardCurveScopedQuote["coverageState"]) =>
    state === "quoted" ? 3 : state === "unquoted" ? 2 : 1;
  const timestamp = (quote: EexForwardCurveScopedQuote) => {
    const parsed = Date.parse(quote.marketTimestamp ?? quote.receivedAt);
    return Number.isFinite(parsed) ? parsed : 0;
  };
  return coverageRank(a.coverageState) - coverageRank(b.coverageState)
    || timestamp(a) - timestamp(b)
    || a.scopeId.localeCompare(b.scopeId);
}

const UTC_DAY_MS = 24 * 60 * 60 * 1_000;
const PERIOD_KIND_RANK: Record<EexMatrixDeliveryPeriod["kind"], number> = {
  day: 0,
  weekend: 1,
  week: 2,
  month: 3,
  quarter: 4,
  season: 5,
  year: 6,
  spread: 7,
};

function compareDeliveryPeriods(
  a: EexMatrixDeliveryPeriod,
  b: EexMatrixDeliveryPeriod
): number {
  const aWindow = eexForwardCurveDeliveryWindow(a);
  const bWindow = eexForwardCurveDeliveryWindow(b);
  if (aWindow && bWindow) {
    return aWindow.startMs - bWindow.startMs
      || aWindow.endExclusiveMs - bWindow.endExclusiveMs
      || PERIOD_KIND_RANK[a.kind] - PERIOD_KIND_RANK[b.kind]
      || a.key.localeCompare(b.key);
  }
  if (aWindow) return -1;
  if (bWindow) return 1;
  return a.sortKey.localeCompare(b.sortKey) || a.key.localeCompare(b.key);
}

function availableForwardPeriods(
  periods: readonly EexMatrixDeliveryPeriod[],
  availablePeriodKeys: ReadonlySet<string>
): EexMatrixDeliveryPeriod[] {
  return periods
    .filter((period) => availablePeriodKeys.has(period.key))
    .filter((period) => ["month", "quarter", "season", "year"].includes(period.kind))
    .sort(compareDeliveryPeriods);
}

function parseUtcDate(value: string | null): number | null {
  if (!value) return null;
  const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(value);
  if (!match) return null;
  const year = Number(match[1]);
  const monthIndex = Number(match[2]) - 1;
  const day = Number(match[3]);
  const parsed = Date.UTC(year, monthIndex, day);
  if (!Number.isFinite(parsed)) return null;
  const check = new Date(parsed);
  return check.getUTCFullYear() === year
    && check.getUTCMonth() === monthIndex
    && check.getUTCDate() === day
    ? parsed
    : null;
}

function parseUtcMonth(value: string): number | null {
  const match = /^(\d{4})-(\d{2})$/.exec(value);
  if (!match) return null;
  const year = Number(match[1]);
  const monthIndex = Number(match[2]) - 1;
  if (monthIndex < 0 || monthIndex > 11) return null;
  const parsed = Date.UTC(year, monthIndex, 1);
  const check = new Date(parsed);
  return Number.isFinite(parsed)
    && check.getUTCFullYear() === year
    && check.getUTCMonth() === monthIndex
    ? parsed
    : null;
}

function formatUtcMonth(value: number): string {
  const date = new Date(value);
  return `${date.getUTCFullYear()}-${String(date.getUTCMonth() + 1).padStart(2, "0")}`;
}
